import L from "leaflet";
import styles from './DistrictMap.module.css';
import { MapContainer, GeoJSON, Rectangle, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-simple-map-screenshoter";
import { useEffect, useState, useRef, useCallback } from "react";

function DistrictMap({ distrito, onSelectDistrict, onImageReady }) {
  const [geoData, setGeoData] = useState(null);
  const layersRef = useRef({});
  const mapRef = useRef(null);

  function MapRefCatcher({ onReady }) {
    const map = useMap();
    useEffect(() => {
      if (map) onReady?.(map);
    }, [map, onReady]);
    return null;
  }

  // Captura usando leaflet-simple-map-screenshoter
  const captureMap = useCallback(async () => {
    if (!mapRef.current) return;

    const screenshoter = L.simpleMapScreenshoter({
      cropImageByInnerWH: true,
      hidden: true,
      mimeType: 'image/png',
      quality: 1
    }).addTo(mapRef.current);

    try {
      const blob = await screenshoter.takeScreen();
      const reader = new FileReader();
      reader.onload = () => {
        onImageReady?.(reader.result);
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error("Erro ao capturar mapa:", err);
    }
  }, [onImageReady]);

  // Carrega GeoJSON
  useEffect(() => {
    fetch("/distritos.geojson")
      .then(res => res.json())
      .then(data => setGeoData(data))
      .catch(err => console.error("Erro ao carregar GeoJSON:", err));
  }, []);

  // Captura inicial quando o geoData estiver carregado
  useEffect(() => {
    if (!mapRef.current || !geoData) return;
    setTimeout(captureMap, 500);
  }, [geoData, captureMap]);

  // Captura quando o distrito mudar
  useEffect(() => {
    if (!mapRef.current || !geoData) return;
    setTimeout(captureMap, 500);
  }, [distrito, geoData, captureMap]);

  const bounds = [
    [-90, -180],
    [90, 180]
  ];

  function FitBoundsControl({ geoData, distrito }) {
    const map = useMap();
    useEffect(() => {
      if (!geoData || !map) return;
      if (distrito && distrito !== "Todos") {
        const selectedFeature = geoData.features.find(
          (feature) => feature.properties.distrito === distrito
        );
        if (selectedFeature) {
          const layer = L.geoJSON(selectedFeature);
          map.fitBounds(layer.getBounds());
        }
      } else {
        const geojsonLayer = L.geoJSON(geoData);
        map.fitBounds(geojsonLayer.getBounds());
      }
    }, [geoData, map, distrito]);
    return null;
  }

  const onEachFeature = (feature, layer) => {
    const nomeDistrito = feature.properties.distrito || "Distrito";
    layersRef.current[nomeDistrito] = layer;
    layer.on({
      click: () => onSelectDistrict?.(nomeDistrito),
    });
  };

  // Atualiza estilo dos distritos
  useEffect(() => {
    if (!geoData) return;
    Object.entries(layersRef.current).forEach(([nome, layer]) => {
      if (nome === distrito) {
        layer.setStyle({
          fillColor: "#E63946",
          fillOpacity: 0.7,
          color: "#222",
          weight: 2,
        });
      } else {
        layer.setStyle({
          fillColor: "#AF8500",
          fillOpacity: 0.5,
          color: "white",
          weight: 1,
        });
      }
    });
  }, [distrito, geoData]);

  return (
    <div className={styles.districtMap}>
      <MapContainer
        center={[-20.3336, -43.7686]}
        zoom={10}
        style={{ width: "100%", height: "100%" }}
        preferCanvas={false} // SVG é suportado pelo screenshoter
      >
        <MapRefCatcher onReady={(mapInstance) => { mapRef.current = mapInstance; }} />

        {/* Fundo cinza */}
        <Rectangle bounds={bounds} pathOptions={{ fillColor: "#f4f4f4", fillOpacity: 1, stroke: false }} />

        {geoData && (
          <>
            <GeoJSON
              data={geoData}
              style={() => ({
                color: "white",
                weight: 1,
                fillColor: "#AF8500",
                fillOpacity: 0.5
              })}
              onEachFeature={onEachFeature}
            />
            <FitBoundsControl geoData={geoData} distrito={distrito} />
          </>
        )}
      </MapContainer>
    </div>
  );
}

export default DistrictMap;
